import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Area,
  AreaChart,
} from "recharts";
import { fmtUSD, fmtShort, fmtMonthLabel } from "../lib/format";

export default function GrowthChart({ rows, lang = "it", height = 340, series = "single" }) {
  if (!rows || rows.length === 0) return null;

  const data = rows.map((r, i) => ({
    m: i + 1,
    label: fmtMonthLabel(r, lang),
    capital: Math.round(r.capital_end),
    liquidity: Math.round(r.liquidity_end),
    total: Math.round(r.capital_end + r.liquidity_end),
  }));

  const yearTicks = data.filter((d) => rows[d.m - 1].is_year_start).map((d) => d.m);

  return (
    <div className="wc-chart" data-testid="growth-chart">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
          <defs>
            <linearGradient id="grad-capital" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f5c76e" stopOpacity={0.55} />
              <stop offset="100%" stopColor="#f5c76e" stopOpacity={0.04} />
            </linearGradient>
            <linearGradient id="grad-total" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7dd18c" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#7dd18c" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#262b38" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="m"
            ticks={yearTicks}
            tickFormatter={(m) => String(rows[m - 1]?.year || "")}
            stroke="#6f6a5f"
            tick={{ fill: "#a49b8a", fontSize: 11 }}
          />
          <YAxis
            stroke="#6f6a5f"
            tick={{ fill: "#a49b8a", fontSize: 11 }}
            tickFormatter={(v) => fmtShort(v, lang)}
            width={60}
          />
          <Tooltip
            contentStyle={{
              background: "#141821",
              border: "1px solid #343b4c",
              borderRadius: 10,
              color: "#f2e9d8",
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 12,
            }}
            labelFormatter={(m) => fmtMonthLabel(rows[m - 1], lang)}
            formatter={(value, name) => [fmtUSD(value, lang), name]}
          />
          <Legend
            verticalAlign="top"
            height={30}
            wrapperStyle={{ color: "#a49b8a", fontSize: 12 }}
          />
          <Area
            type="monotone"
            name={lang === "it" ? "Totale (capitale + liquidità)" : "Total (capital + cash)"}
            dataKey="total"
            stroke="#7dd18c"
            strokeWidth={1.5}
            fill="url(#grad-total)"
          />
          <Area
            type="monotone"
            name={lang === "it" ? "Capitale" : "Capital"}
            dataKey="capital"
            stroke="#f5c76e"
            strokeWidth={2.5}
            fill="url(#grad-capital)"
          />
          <Line
            type="monotone"
            name={lang === "it" ? "Liquidità" : "Cash"}
            dataKey="liquidity"
            stroke="#ffa64d"
            strokeWidth={1.5}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ComparisonChart({ rowsA, rowsB, lang = "it", height = 360 }) {
  const len = Math.max(rowsA?.length || 0, rowsB?.length || 0);
  const data = Array.from({ length: len }, (_, i) => ({
    m: i + 1,
    label: fmtMonthLabel(rowsA?.[i] || rowsB?.[i], lang),
    A: rowsA?.[i] ? Math.round(rowsA[i].capital_end + rowsA[i].liquidity_end) : null,
    B: rowsB?.[i] ? Math.round(rowsB[i].capital_end + rowsB[i].liquidity_end) : null,
  }));

  const yearTicks = data
    .filter((d, i) => rowsA?.[i]?.is_year_start || rowsB?.[i]?.is_year_start)
    .map((d) => d.m);

  return (
    <div className="wc-chart" data-testid="comparison-chart">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
          <CartesianGrid stroke="#262b38" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="m"
            ticks={yearTicks}
            tickFormatter={(m) => String((rowsA?.[m - 1] || rowsB?.[m - 1])?.year || "")}
            stroke="#6f6a5f"
            tick={{ fill: "#a49b8a", fontSize: 11 }}
          />
          <YAxis
            stroke="#6f6a5f"
            tick={{ fill: "#a49b8a", fontSize: 11 }}
            tickFormatter={(v) => fmtShort(v, lang)}
            width={60}
          />
          <Tooltip
            contentStyle={{
              background: "#141821",
              border: "1px solid #343b4c",
              borderRadius: 10,
              color: "#f2e9d8",
              fontSize: 12,
            }}
            labelFormatter={(m) => fmtMonthLabel(rowsA?.[m - 1] || rowsB?.[m - 1], lang)}
            formatter={(value) => fmtUSD(value, lang)}
          />
          <Legend
            verticalAlign="top"
            height={30}
            wrapperStyle={{ color: "#a49b8a", fontSize: 12 }}
          />
          <Line
            type="monotone"
            name={lang === "it" ? "Scenario A" : "Scenario A"}
            dataKey="A"
            stroke="#f5c76e"
            strokeWidth={2.5}
            dot={false}
          />
          <Line
            type="monotone"
            name={lang === "it" ? "Scenario B" : "Scenario B"}
            dataKey="B"
            stroke="#7dd18c"
            strokeWidth={2.5}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
